<template>
  <div class="container">
    <div class="box-title" style="display:flex;justify-content: space-between">
      <h4>
        <i class="fal fa-box-open"></i>
        ຄຳຮ້ອງຊົດເຊີຍພັດສະດຸ
        <span class="countTotal">({{ pagination.total || 0 }})</span>
      </h4>
      <div class="claim-head-actions">
        <button class="button is-info" @click="$router.push({ name: 'claim.reports' })">ລາຍງານ</button>
        <!--
          Filing on a customer's behalf is the filing desk's own work, and the control is gated by
          the SAME authority as its route. Not a second rule: both ask `$can` for `file-claim`, so a
          button that appears is a screen that opens.
        -->
        <button v-if="mayFile" class="button is-primary" @click="onCreate">ເປີດຄຳຮ້ອງແທນລູກຄ້າ</button>
      </div>
    </div>

    <!--
      Why they are here instead of where they clicked. A route refused for authority sends them to
      their own queue rather than to a page-not-found: the screen exists, it is the other desk's.
    -->
    <div class="box claim-blocked" v-if="blockedNotice">
      <i class="fal fa-info-circle"></i> {{ blockedNotice }}
    </div>

    <!--
      Grouped into the five stages the office names a case by, not by the raw state. Fifteen
      states is a process; five stages is a queue somebody can work from.
    -->
    <div class="box claim-groups">
      <button
        v-for="group in groups"
        :key="group.id"
        class="button"
        :class="{ 'is-primary': selectedGroup === group.id }"
        @click="selectedGroup = group.id; fetchClaims(1)"
      >
        {{ group.name }}
      </button>
      <p class="claim-groups__hint" v-if="selectedGroupHint">{{ selectedGroupHint }}</p>
    </div>

    <div class="box">
      <div class="claim-filter">
        <div class="claim-filter__field">
          <label class="label">ຄົ້ນຫາ:</label>
          <input class="input" v-model="search" placeholder="ເລກຄຳຮ້ອງ ຫຼື ເລກບິນ" @keyup.enter="fetchClaims(1)">
        </div>

        <div class="claim-filter__field">
          <label class="label">ສະຖານະ:</label>
          <select class="input" v-model="selectedStatus" @change="fetchClaims(1)">
            <option value="">ທັງໝົດ</option>
            <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
          </select>
        </div>

        <div class="claim-filter__field">
          <label class="label">ປະເພດ:</label>
          <select class="input" v-model="selectedKind" @change="fetchClaims(1)">
            <option value="">ທັງໝົດ</option>
            <option v-for="k in kindOptions" :key="k.value" :value="k.value">{{ k.label }}</option>
          </select>
        </div>

        <div class="claim-filter__field">
          <label class="label">ຜູ້ຮັບຜິດຊອບ:</label>
          <select class="input" v-model="selectedParty" @change="fetchClaims(1)">
            <option value="">ທັງໝົດ</option>
            <option v-for="p in partyOptions" :key="p.value" :value="p.value">{{ p.label }}</option>
          </select>
        </div>

        <div class="claim-filter__field claim-filter__field--wide">
          <label class="label">ສາຂາທີ່ຮັບຜິດຊອບ:</label>
          <!--
            `model-list-select`, not `vs-select` — the vuesax control's inner
            input has a fixed intrinsic width that overflows a filter row
            (repo HARD RULE 1). A claim has no responsible branch until it is
            assessed, so this filter necessarily hides unassessed claims: before
            assessment no branch is liable, and listing them under one would
            assert a decision nobody has made.
          -->
          <model-list-select
            v-model="selectedBranch"
            :list="branches"
            option-text="name"
            option-value="id"
            selectedItem="name"
            placeholder="ທັງໝົດ"
            @input="fetchClaims(1)">
          </model-list-select>
        </div>

        <div class="claim-filter__field">
          <label class="label">ຈາກວັນທີ:</label>
          <input class="input" type="date" v-model="fromDate" @change="fetchClaims(1)">
        </div>

        <div class="claim-filter__field">
          <label class="label">ຫາວັນທີ:</label>
          <input class="input" type="date" v-model="toDate" @change="fetchClaims(1)">
        </div>

        <div class="claim-filter__actions">
          <button class="button is-dark" @click="resetFilters">ລ້າງ</button>
        </div>
      </div>
    </div>

    <div class="center">
      <vs-table>
        <template #thead>
          <vs-tr>
            <!--
              ເລກບິນ ແລະ ວັນທີ ນຳໜ້າ: ສອງຢ່າງນີ້ຄືສິ່ງທີ່ພະນັກງານກວາດຕາຫາ — ເລກຄຳຮ້ອງ
              ເປັນເລກພາຍໃນທີ່ອ້າງອີງພາຍຫຼັງ ຈຶ່ງຍ້າຍໄປທ້າຍສຸດ. ລຳດັບໃນ `#tbody`
              ຕ້ອງຕົງກັນແຖວຕໍ່ແຖວ — ຕາຕະລາງນີ້ບໍ່ໄດ້ຜູກຫົວກັບຂໍ້ມູນ.
            -->
            <vs-th>ເລກບິນ</vs-th>
            <vs-th>ວັນທີ</vs-th>
            <vs-th>ຜູ້ຮ້ອງຂໍ</vs-th>
            <vs-th>ປະເພດ</vs-th>
            <vs-th>ສະຖານະ</vs-th>
            <vs-th>ລູກຄ້າຂໍ</vs-th>
            <vs-th>ປະເມີນ</vs-th>
            <vs-th>ຜູ້ຮັບຜິດຊອບ</vs-th>
            <vs-th>ເລກຄຳຮ້ອງ</vs-th>
            <vs-th></vs-th>
          </vs-tr>
        </template>

        <!--
          `#tbody`, not a bare `<template>`. This vuesax renders rows only from
          the NAMED slot — an unnamed one is silently dropped and the table shows
          its own "No matching records found" while the data sits right there.
          Every other table in this app uses `#tbody`; nothing at build time
          catches the difference.
        -->
        <template #tbody>
          <vs-tr :key="row.id" :data="row" v-for="row in claims">
            <vs-td>
              {{ row.shipment_number || '-' }}
              <!--
                A claim can hold several boxes, and the row can only show one bill. The count is
                what tells an officer whether opening this claim means reading one case or five —
                and the tally is what says how far through it is.
              -->
              <span class="claim-parcels" v-if="row.parcels && row.parcels.total > 1">
                +{{ row.parcels.total - 1 }} ພັດສະດຸ
              </span>
            </vs-td>
            <vs-td>{{ moment(row.created_at).format('DD/MM/YYYY') }}</vs-td>
            <!--
              Which end of the bill the claimant is, so a desk can be worked from the list. The row
              carried a telephone number and nothing else, and an officer picking up a case had to
              open it — or telephone and ask — to find out whether they were talking to the shop that
              sent the parcel or the customer who never got it.

              Blank where none is recorded: a claim whose parcels disagree genuinely has no single
              answer, and one filed before the side was recorded has none at all.
            -->
            <vs-td>{{ claimantRoleLabel(row.claimant_role) }}</vs-td>
            <vs-td>{{ kindLabel(row.claim_kind) }}</vs-td>
            <vs-td>
              {{ statusLabel(row.status) }}
              <!--
                `pending` means both "just filed" and "we are waiting on the
                claimant" — opposite things for a queue. The contract publishes
                `action_required_when` precisely so this is not guessed.
              -->
              <span v-if="isWaitingOnClaimant(row)" class="claim-waiting">ລໍຖ້າລູກຄ້າສົ່ງເອກະສານ</span>
              <!--
                The status is the LEAST finished parcel's — a claim with two boxes paid and one
                waiting is waiting, not closed. `outcome` is the other half: it is the only thing
                that can say a claim holds two answers at once.
              -->
              <span v-if="row.outcome && row.parcels && row.parcels.total > 1" class="claim-outcome">
                {{ outcomeLabel(row.outcome) }}
              </span>
            </vs-td>
            <!-- Both figures. The assessment never overwrites what was asked for, and the gap
                 between them is what a disagreement is about. -->
            <vs-td>{{ formatMoney(row.claimed_amount) }}</vs-td>
            <vs-td>{{ formatMoney(row.amount) }}</vs-td>
            <!--
              Who carries the cost. It was a filter and never a column, so an assessor could narrow
              the queue by it and still not see it on the rows that came back. Blank until the parcel
              is assessed, and blank on a claim whose parcels named different parties — there is no
              single answer then, and picking one would be worse than showing none.
            -->
            <vs-td>{{ partyKindLabel(row.liable_party_kind) }}</vs-td>
            <vs-td>{{ row.claim_no }}</vs-td>
            <vs-td>
              <button class="button is-info is-small" @click="onDetail(row)">ລາຍລະອຽດ</button>
            </vs-td>
          </vs-tr>
        </template>

        <template #footer>
          <pagination
            v-if="pagination.last_page > 1"
            :pagination="pagination"
            :offset="offset"
            @paginate="fetchClaims"
          ></pagination>
        </template>
      </vs-table>

      <div v-if="!isLoading && !claims.length" class="claim-empty">ບໍ່ພົບຄຳຮ້ອງຕາມເງື່ອນໄຂນີ້</div>
    </div>
  </div>
</template>

<script>
import { ModelListSelect } from 'vue-search-select';
import ListClaims from '@/Mixins/Claim/ListClaims';
import pagination from '@/Plugins/Paginations/Pagination.vue';

export default {
  name: 'ListClaim',
  components: { pagination, ModelListSelect },
  mixins: [ListClaims],

  data: () => ({
    offset: 10,
  }),

  created() {
    // Everything renderable comes from here — enumerations, labels, bands, the
    // upload ceiling. Nothing in this feature restates any of them.
    this.$store.dispatch('Claim/load').finally(() => this.fetchClaims(1));
    this.fetchBranches();
  },

  methods: {
    kindLabel(value) {
      const found = this.kindOptions.find((k) => k.value === value);
      return found ? found.label : value;
    },
    onDetail(row) {
      this.$router.push({ name: 'claim.detail', params: { id: row.id } });
    },
    onCreate() {
      this.$router.push({ name: 'claim.create' });
    },
  },
};
</script>

<style lang="scss" scoped>
.claim-parcels {
  display: block;
  font-size: 11px;
  color: #7a7a7a;
}

.claim-outcome {
  display: block;
  font-size: 11px;
  color: #1f5f8b;
}

.claim-head-actions {
  display: flex;
  gap: 8px;

  // The global `.button { background: $primary-color }` beats Bulma's is-*
  // modifiers, so a non-primary colour has to be set in scoped CSS.
  .button.is-info { background: #1abc9c; &:hover { background: #159b81; } }
}

.claim-groups {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.claim-groups__hint {
  flex-basis: 100%;
  margin: 2px 0 0;
  font-size: 13px;
  color: #7a7a7a;
}

.claim-party-tag,
.claim-channel-tag {
  display: inline-block;
  margin-left: .4rem;
  padding: 0 .45rem;
  border-radius: 3px;
  font-size: .72rem;
  line-height: 1.5rem;
  vertical-align: middle;
}

/* The two are coloured apart on purpose: they answer different questions, and a reader who took
   one for the other would be reading who gets paid off a mark about who typed it in. */
.claim-party-tag { color: #276749; background: #f0fff4; }
.claim-channel-tag { color: #2a4365; background: #ebf8ff; }

.claim-blocked {
  color: #946c00;
  background: #fffaeb;
  border-left: 3px solid #ffdd57;
}

.claim-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;

  &__field {
    min-width: 180px;

    &--wide { min-width: 240px; }
  }

  &__actions {
    // The global `.button { background: $primary-color }` rule overrides Bulma's
    // is-* modifiers, so a secondary button needs its colour set in scoped CSS.
    .button.is-dark {
      background: #34495e;
      &:hover { background: #2b3a4a; }
    }
  }
}

.claim-waiting {
  display: block;
  font-size: 11px;
  color: #b7791f;
}

.claim-empty {
  padding: 24px;
  text-align: center;
  color: #7a7a7a;
}
</style>
